import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Phone, ChevronDown, ChevronUp } from 'lucide-react';

const FAQ = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqData = [
    {
      question: 'Is the taxi service available 24/7?',
      answer: 'Yes, our taxi service operates 24/7 to ensure that you can book a taxi at any time that suits your travel needs.'
    },
    {
      question: 'How easy is it to book taxi?',
      answer: 'Very! Our taxi booking form, placed on the top of the page, makes your transfer reservation very simple and easy. You can make a booking in 2 minutes.'
    },
    {
      question: 'Ways to book a Taxi Service',
      answer: 'You can book a taxi with Advait Tour and Travels. Also you can go to simple Contact form for booking and fill out the contact form. Or call by phone +91-79986-66660.'
    },
    {
      question: 'What if my travel plans change?',
      answer: "If there are any changes to your travel plans, please contact our customer service as soon as possible. We'll do our best to accommodate your requests, taking into consideration availability and timing."
    },
    {
      question: 'Can I get the contact details of the driver before my journey?',
      answer: 'Yes, we provide driver details, including their name and contact number, a few hours before your scheduled pick-up time to facilitate smooth communication.'
    },
    {
      question: 'How can I make the payment for the taxi service?',
      answer: 'We offer various payment methods for your convenience. You can inquire about the available payment options when booking the taxi.'
    },
    {
      question: 'What details will I receive once I book a taxi?',
      answer: 'Once you book a taxi, you will receive confirmation details, including the type of vehicle, estimated duration of the trip, total fare, and the date and time of your journey.'
    },
    {
      question: 'Can I specify any special requirements during the booking process?',
      answer: 'Yes, during the booking inquiry, you can communicate any specific requirements you may have, such as a preference for a particular vehicle type, extra luggage space, or other special requests.'
    },
    {
      question: 'How can I inquire about the availability of a taxi for a specific date and time?',
      answer: 'You can contact our customer service team through our website, hotline, or email. They will provide you with information about vehicle availability, travel time estimates, and pricing.'
    },
    {
      question: 'What types of vehicles are available for booking?',
      answer: 'We offer a variety of vehicles, including sedans, SUVs, and even larger vehicles like tempo travelers. The availability may vary based on your specific requirements.'
    },
    {
      question: 'Are cabs readily available at Chandigarh airport?',
      answer: 'Yes, cabs are generally available at Chandigarh airport. However, for added convenience, you can pre-book a taxi online to ensure a safe and timely pick-up.'
    },
    {
      question: 'How do I book a taxi from Chandigarh airport?',
      answer: 'Booking a taxi from Chandigarh airport is easy. You can either book online through our website or contact our customer service hotline.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <SEO
        title="FAQ - Frequently Asked Questions | Advait Tour and Travels"
        description="Find answers to frequently asked questions about Advait Tour and Travels taxi services, booking process, payment methods, and more."
        keywords="advait tour and travels faq, taxi booking questions, chandigarh taxi faq, cab service questions, travel queries"
        canonicalUrl={`${window.location?.origin}/faq`}
        ogTitle="FAQ - Frequently Asked Questions | Advait Tour and Travels"
        ogDescription="Find answers to frequently asked questions about our taxi services, booking process, and more."
        ogUrl={`${window.location?.origin}/faq`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqData?.map(faq => ({
            "@type": "Question",
            "name": faq?.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq?.answer
            }
          }))
        }}
      />
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="py-20">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-20 mb-12">
            <div className="container mx-auto px-4 text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Frequently Asked Questions
              </h1>
              <p className="text-xl text-white max-w-3xl mx-auto">
                Find answers to common questions about Advait Tour and Travels
              </p>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="container mx-auto px-4 mb-16">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-4">
                {faqData?.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
                  >
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 className="text-lg font-bold text-gray-900 pr-4">
                        {faq?.question}
                      </h3>
                      {openIndex === index ? (
                        <ChevronUp className="text-blue-600 flex-shrink-0" size={24} />
                      ) : (
                        <ChevronDown className="text-gray-400 flex-shrink-0" size={24} />
                      )}
                    </button>
                    {openIndex === index && (
                      <div className="px-6 pb-5">
                        <p className="text-gray-700 leading-relaxed">
                          {faq?.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-12">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                We're here for you 24/7!
              </h2>
              <p className="text-xl text-white mb-6">
                Give us a call anytime
              </p>
              <a
                href="tel:+917998666660"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-2xl rounded-full hover:bg-gray-100 transition-colors duration-300"
              >
                <Phone className="w-6 h-6" />
                +91-79986-66660
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default FAQ;